/**
 * useAnonEntityOwnership.ts
 * Hook to check if the current user owns an anonymous entity by checking for stored edit token.
 * 
 * For anonymous entities (user_id = 0), ownership is determined by possession of the edit token.
 * This hook checks if a token exists in the vault for the given entity.
 */

import { useState, useEffect } from "react";
import { loadAnonToken, AnonEntity } from "util/anonTokenVault";

/**
 * Check if the current user can edit an anonymous entity
 * @param entityType - Type of entity (impact, comment, solution, feedback, action)
 * @param entityId - ID of the entity
 * @param userId - The entity's user_id (0 for anonymous)
 * @param privacy - The entity's privacy level
 * @returns true if user has the edit token for this anonymous entity
 */
export function useAnonEntityOwnership(
  entityType: "impact" | "teabate" | "comment" | "solution" | "feedback" | "action" | "collection" | null,
  entityId: number | string | undefined,
  userId: number | undefined | null,
  privacy?: string
): boolean {
  const [hasToken, setHasToken] = useState(false);

  useEffect(() => {
    if ((userId !== 0 && userId !== null && userId !== undefined) || privacy !== "anonymous" || !entityType || !entityId) {
      setHasToken(false);
      return;
    }

    const anonEntityMap: Record<string, AnonEntity> = {
      impact: "impact",
      teabate: "teabate",
      comment: "comment",
      solution: "solution",
      feedback: "feedback",
      action: "action",
      collection: "collection",
    };

    const anonType = anonEntityMap[entityType];
    if (!anonType) {
      setHasToken(false);
      return;
    }

    // Check if we have a token for this entity
    void (async () => {
      try {
        const token = await loadAnonToken(anonType, entityId);
        setHasToken(!!token);
      } catch {
        setHasToken(false);
      }
    })();
  }, [entityType, entityId, userId, privacy]);

  return hasToken;
}

